import { useState, useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';

import { OpenDayOptionsType, GetDayTooltipType, HasDayOptionsType } from '../types';
import PlusIcon from '../../assets/svg/Icons/16/note-add.svg';
import PlusIconBlue from '../../assets/svg/Icons/16/note-add-blue.svg';
import InfoIcon from '../../assets/svg/Icons/16/info-gray.svg';
import InfoIconBlue from '../../assets/svg/Icons/16/info-blue.svg';
import { FormattedDateString } from '../types/FormattedDateString';
import Tooltip from '../../components/Tooltip';

type ContainerProps = {
  hoverComponent?: any;
};

const VisibleContainer = styled.div<ContainerProps>`
  margin-left: ${(props): string => props.theme.spacing.p8};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 100%;
  height: 18px;
`;

const InvisibleContainer = styled(VisibleContainer)`
  display: none;

  ${(props) => props.hoverComponent}:hover & {
    display: flex;
  }
`;

const Clickable = styled.div`
  cursor: pointer;
  display: flex;
`;

type Props = {
  openDayOptions: OpenDayOptionsType;
  getDayOptionsTooltip: GetDayTooltipType;
  hasDayOptions: HasDayOptionsType;
  day: FormattedDateString;
  readonly?: boolean;
} & ContainerProps;

const HeaderDayOptions: React.FC<React.PropsWithChildren<Props>> = ({
  day,
  openDayOptions,
  getDayOptionsTooltip,
  hasDayOptions,
  readonly,
  hoverComponent,
}) => {
  const componentWithTooltip = useRef();
  const [isHovered, setIsHovered] = useState(false);
  const hasOptions = hasDayOptions({ day });
  const Container = hasOptions ? VisibleContainer : InvisibleContainer;

  if (!hasOptions && readonly) {
    return null;
  }

  const toggleHover = (isHovered: boolean): void => {
    setIsHovered(isHovered);
    componentWithTooltip.current && ReactTooltip.hide(componentWithTooltip.current);
  };

  return (
    <Container
      hoverComponent={hoverComponent}
      onMouseEnter={(): void => toggleHover(true)}
      onMouseLeave={(): void => toggleHover(false)}>
      {!hasOptions && (
        <Clickable
          ref={componentWithTooltip}
          onClick={(): void => openDayOptions({ day })}
          data-tip="Add a note or edit options"
          data-place="bottom">
          {!isHovered && <PlusIcon />}
          {isHovered && <PlusIconBlue />}
        </Clickable>
      )}

      {hasOptions && (
        <Clickable
          ref={componentWithTooltip}
          onClick={(): void => openDayOptions({ day })}
          data-tip={getDayOptionsTooltip({ day })}
          data-place="bottom">
          {!isHovered && <InfoIcon />}
          {isHovered && <InfoIconBlue />}
        </Clickable>
      )}
      <Tooltip attachToBody />
    </Container>
  );
};

export default HeaderDayOptions;
